import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";

import { api } from "../api/client";
import { PresenceStore, type Present, type PresenceState } from "./presence";

/**
 * Live updates (DECISIONS #9). One WebSocket per tab. The server only says *what* changed
 * ("ticket ASP-000123", "your alerts", "chats"); we refetch through the normal API, so permissions
 * are always checked there. If the socket drops we reconnect with backoff and refetch
 * everything once, so nothing missed while offline is lost.
 *
 * Support staff also share presence on the tickets they have open (DECISIONS #51).
 */

type LiveMessage =
  | { type: "ticket"; ticket: string }
  | { type: "alerts" }
  | { type: "chats" }
  | { type: "subscribed"; ticket: string }
  | { type: "presence"; ticket: string; user: { id: string; name: string }; state: PresenceState | "left" }
  | { type: "error"; ticket?: string; message?: string };

interface Live {
  /** Hear about changes to a ticket you're looking at. Returns a function to stop. */
  watch(reference: string): () => void;
  /** Tell the other staff on this ticket you're here, typing, or gone. */
  present(reference: string, state: PresenceState | "left"): void;
  presence: PresenceStore;
}

const LiveContext = createContext<Live | null>(null);
const MAX_DELAY_MS = 30_000;
// While a ticket is open, say so this often (others forget you after 50 seconds of silence).
const HEARTBEAT_MS = 20_000;
const TYPING_EVERY_MS = 3_000;

/**
 * Where to connect, with a one-minute ticket. Firebase Hosting can't carry WebSockets, so in
 * production the API names its own Cloud Run address (DECISIONS #42); locally it's this host.
 */
async function liveAddress(): Promise<string> {
  const { url, ticket } = await api<{ url: string | null; ticket: string }>("/api/live/ticket", {
    method: "POST",
  });
  const scheme = window.location.protocol === "https:" ? "wss" : "ws";
  const base = url ?? `${scheme}://${window.location.host}/api/live`;
  return `${base}?ticket=${encodeURIComponent(ticket)}`;
}

export function LiveProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const socket = useRef<WebSocket | null>(null);
  const watching = useRef(new Map<string, number>());
  // Tickets we're showing presence on, and how: re-announced when the socket reconnects.
  const presentOn = useRef(new Map<string, PresenceState>());
  const [presence] = useState(() => new PresenceStore());

  useEffect(() => {
    // Tests and very old browsers: the app still works, just without live updates.
    if (import.meta.env.MODE === "test" || typeof WebSocket === "undefined") return;
    let stopped = false;
    let attempts = 0;
    let timer: number | undefined;

    function onMessage(message: LiveMessage) {
      if (message.type === "ticket") {
        const ref = message.ticket;
        void queryClient.invalidateQueries({ queryKey: ["tickets", "detail", ref] });
        void queryClient.invalidateQueries({ queryKey: ["tickets", "timeline", ref] });
        void queryClient.invalidateQueries({ queryKey: ["tickets", "list"] });
      } else if (message.type === "alerts") {
        void queryClient.invalidateQueries({ queryKey: ["alerts"] });
      } else if (message.type === "chats") {
        void queryClient.invalidateQueries({ queryKey: ["chats"] });
      } else if (message.type === "presence") {
        const fresh = presence.update(message.ticket, message.user, message.state);
        // Someone new arrived: say we're here too, so they don't wait for our next heartbeat.
        const mine = presentOn.current.get(message.ticket);
        if (fresh && mine) send({ presence: message.ticket, state: mine });
      }
    }

    function send(message: Record<string, string>) {
      if (socket.current?.readyState === WebSocket.OPEN) socket.current.send(JSON.stringify(message));
    }

    function retry() {
      if (stopped) return;
      const delay = Math.min(MAX_DELAY_MS, 1000 * 2 ** attempts) + Math.random() * 500;
      attempts += 1;
      timer = window.setTimeout(connect, delay);
    }

    function connect() {
      liveAddress().then((address) => {
        if (!stopped) open(address);
      }, retry);
    }

    function open(address: string) {
      const ws = new WebSocket(address);
      socket.current = ws;
      ws.onopen = () => {
        if (attempts > 0) void queryClient.invalidateQueries(); // catch up after a drop
        attempts = 0;
        for (const ref of watching.current.keys()) ws.send(JSON.stringify({ subscribe: ref }));
        for (const [ref, state] of presentOn.current) ws.send(JSON.stringify({ presence: ref, state }));
      };
      ws.onmessage = (event: MessageEvent<string>) => {
        try {
          onMessage(JSON.parse(event.data) as LiveMessage);
        } catch {
          // ignore anything we don't understand
        }
      };
      ws.onclose = () => {
        if (socket.current === ws) socket.current = null;
        presence.clear();
        retry();
      };
    }

    connect();
    const fading = window.setInterval(() => presence.tick(), 5_000);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      window.clearInterval(fading);
      socket.current?.close();
      socket.current = null;
    };
  }, [queryClient, presence]);

  const live = useMemo<Live>(() => {
    function send(message: Record<string, string>) {
      if (socket.current?.readyState === WebSocket.OPEN) socket.current.send(JSON.stringify(message));
    }
    return {
      presence,
      present(reference, state) {
        if (state === "left") presentOn.current.delete(reference);
        else presentOn.current.set(reference, "viewing"); // typing is a moment, not a state to restore
        send({ presence: reference, state });
      },
      watch(reference) {
        const count = watching.current.get(reference) ?? 0;
        watching.current.set(reference, count + 1);
        if (count === 0) send({ subscribe: reference });
        return () => {
          const left = (watching.current.get(reference) ?? 1) - 1;
          if (left > 0) {
            watching.current.set(reference, left);
            return;
          }
          watching.current.delete(reference);
          send({ unsubscribe: reference });
        };
      },
    };
  }, [presence]);

  return <LiveContext.Provider value={live}>{children}</LiveContext.Provider>;
}

/** Refresh this ticket as soon as anyone changes it (while the component is on screen). */
export function useLiveTicket(reference: string): void {
  const live = useContext(LiveContext);
  useEffect(() => (live && reference ? live.watch(reference) : undefined), [live, reference]);
}

const NOBODY: Present[] = [];
const noStore = () => () => undefined;

/**
 * Support staff: share that you have this ticket open, and see who else has. Returns the others
 * and a function to call as you type (it tells them at most every few seconds).
 */
export function usePresence(reference: string, enabled: boolean): { others: Present[]; typing: () => void } {
  const live = useContext(LiveContext);
  const lastTyped = useRef(0);
  useEffect(() => {
    if (!live || !enabled || !reference) return;
    live.present(reference, "viewing");
    const beat = window.setInterval(() => live.present(reference, "viewing"), HEARTBEAT_MS);
    return () => {
      window.clearInterval(beat);
      live.present(reference, "left");
    };
  }, [live, enabled, reference]);
  const others = useSyncExternalStore(live && enabled ? live.presence.subscribe : noStore, () =>
    live && enabled ? live.presence.people(reference) : NOBODY,
  );
  const typing = useCallback(() => {
    if (!live || !enabled) return;
    const now = Date.now();
    if (now - lastTyped.current < TYPING_EVERY_MS) return;
    lastTyped.current = now;
    live.present(reference, "typing");
  }, [live, enabled, reference]);
  return { others, typing };
}
